import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { ApiService } from '../services/api.service';
import { getCookie } from '../utils/cookie.utils';

/** Navigations within this window reuse the last server-validated user instead of blocking on `/user/:id`. */
const USER_REVALIDATE_INTERVAL_MS = 60_000;
let lastValidatedUserId: string | null = null;
let lastValidatedAt = 0;

export const authGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const apiService = inject(ApiService);
  const router = inject(Router);

  const targetUrl = state?.url || '';
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const hash = typeof window !== 'undefined' ? window.location.hash : '';
  const currentUrl = router.url || '';
  const full = `${targetUrl} ${currentPath} ${hash} ${currentUrl}`.toLowerCase();
  
  if (full.includes('signup') || full.includes('sign-up') || full.includes('login')) {
    return true;
  }

  // Check if user has valid token and is authenticated
  const token = apiService.getCurrentAuthToken();
  const currentUser = apiService.getCurrentUser();

  if (!token || !currentUser) {
    const refreshToken = getCookie('refresh_token');
    if (!token && refreshToken && currentUser?.id) {
      return apiService.refreshToken().pipe(
        map(() => true),
        catchError((error) => {
          const status = Number(error?.status ?? 0);
          if (status === 401 || status === 403) {
            if (targetUrl && targetUrl !== '/login') {
              localStorage.setItem('intendedUrl', targetUrl);
            }
            apiService.clearSessionState({ preservePreferences: true });
            router.navigate(['/login']);
            return of(false);
          }
          return of(true);
        })
      );
    }

    if (targetUrl && targetUrl !== '/login') {
      localStorage.setItem('intendedUrl', targetUrl);
    }
    router.navigate(['/login']);
    return false;
  }

  // Allow direct access to no-organization and no-parking status pages
  if (
    targetUrl.includes('/no-organization') ||
    targetUrl.includes('/no-parking') ||
    currentPath.includes('/no-organization') ||
    currentUrl.includes('/no-organization') ||
    currentPath.includes('/no-parking') ||
    currentUrl.includes('/no-parking')
  ) {
    return true;
  }

  // Check if user_pov is 'none' and trying to access organization dashboard
  // But allow super admin to access /organization/dashboard
  const userPov = sessionStorage.getItem('user_pov');
  const userInfoStr = localStorage.getItem('user_info');
  let isSuperAdmin = false;
  let isAdmin = false;
  if (userInfoStr) {
    try {
      const user = JSON.parse(userInfoStr);
      const userRole = user.role_id || user.role?.name || user.type;
      const normalizedRole = String(userRole || '').toLowerCase();
      const userType = String(user.type || '').toLowerCase();
      if (normalizedRole === 'super admin' || normalizedRole === 'super_admin' ||
          userType === 'super admin' || userType === 'super_admin' || userType === 'superadmin' ||
          user.email === 'system_admin@kormoan.com' || userPov === 'super-admin') {
        isSuperAdmin = true;
      }
      if (normalizedRole === 'admin' || userType === 'admin' || userPov === 'admin') {
        isAdmin = true;
      }
    } catch (e) { }
  }

  // Also check current role from role service
  const currentRole = currentUser?.current_role || currentUser?.role_name || currentUser?.role?.name;
  if (currentRole) {
    const normalizedCurrentRole = String(currentRole).toLowerCase();
    if (normalizedCurrentRole === 'super admin' || normalizedCurrentRole === 'super_admin' || normalizedCurrentRole === 'superadmin') {
      isSuperAdmin = true;
    }
    if (normalizedCurrentRole === 'admin') {
      isAdmin = true;
    }
  }

  // Only redirect to /dashboard if user_pov is 'none' AND user is NOT super admin
  if (userPov === 'none' && !isSuperAdmin && (targetUrl.includes('/organization') || currentPath.includes('/organization') || currentUrl.includes('/organization'))) {
    router.navigate(['/dashboard']);
    return false;
  }

  // Check if the URL has an invalid orgId (like 'dashboard' instead of a UUID)
  const segments = targetUrl.split('?')[0].split('/').filter(seg => seg.length > 0);
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  if (segments.length >= 2 && segments[0] === 'organization' && !isSuperAdmin) {
    const orgId = segments[1];
    if (!uuidPattern.test(orgId)) {
      // Invalid orgId (like 'dashboard'), redirect to no-organization
      router.navigate(['/no-organization']);
      return false;
    }
  }

  // Check if URL has invalid parkingId
  if (segments.length >= 2 && segments[0] === 'parking' && !isSuperAdmin) {
    const parkingId = segments[1];
    if (!uuidPattern.test(parkingId)) {
      router.navigate(['/no-parking']);
      return false;
    }
  }

  const extractOrgId = (user: any): string | null => {
    if (segments.length >= 2 && segments[0] === 'organization' && uuidPattern.test(segments[1])) {
      return segments[1];
    }
    let org: any = user?.organization;
    let id: any = null;
    if (org && typeof org === 'object') {
      id = org.id || org._id;
    } else if (typeof org === 'string' && org !== 'null' && org !== 'undefined') {
      id = org;
    }
    if (!id && user) {
      id = user.organization_id || user.org_id || user.organizationId;
    }
    if (!id) {
      id = sessionStorage.getItem('organization_id') || localStorage.getItem('organization_id') ||
           sessionStorage.getItem('org_id') || localStorage.getItem('org_id');
    }
    if (!id || id === 'null' || id === 'undefined' || (typeof id === 'string' && id.trim() === '')) {
      return null;
    }
    return String(id).trim();
  };

  const extractAssignedParkings = (user: any): any[] => {
    if (!user) return [];
    const list = user.parkings || user.parkingIds || [];
    if (!Array.isArray(list)) return [];
    return list.filter((p: any) => {
      const pId = typeof p === 'object' ? (p?.id || p?.parking_id || p?._id) : p;
      return !!(pId && pId !== 'null' && pId !== 'undefined' && String(pId).trim().length > 0);
    });
  };

  const checkAccess = (userData: any, isFreshFromServer: boolean) => {
    const normalizedStatus = String(userData?.status || '').toLowerCase();
    const isSuspended =
      normalizedStatus === 'suspend' ||
      normalizedStatus === 'suspended' ||
      normalizedStatus === 'inactive' ||
      userData?.is_active === false ||
      userData?.status === false;

    if (isSuspended) {
      apiService.clearSessionState({ preservePreferences: true });
      return router.createUrlTree(['/login']);
    }

    // Check if user is Super Admin or Admin / Org-level manager
    const uType = String(userData?.type || currentUser?.type || '').toLowerCase();
    const uRole = String(userData?.role?.name || userData?.role_name || currentUser?.role?.name || '').toLowerCase();
    const isSuperAdminUser =
      uType === 'superadmin' ||
      uType === 'super_admin' ||
      uType === 'super admin' ||
      uRole === 'super admin' ||
      userData?.email === 'system_admin@kormoan.com' ||
      userPov === 'super-admin' ||
      isSuperAdmin;
    const isOrgLevelUser =
      uType === 'admin' ||
      uRole === 'admin' ||
      uRole === 'organization admin' ||
      uRole === 'org admin' ||
      uRole === 'manager' ||
      userPov === 'admin' ||
      userPov === 'organization' ||
      userPov === 'active' ||
      userData?.user_pov === 'organization' ||
      userData?.user_pov === 'active' ||
      isAdmin ||
      (Array.isArray(userData?.permissions || userData?.role?.permission) &&
        (userData?.permissions || userData?.role?.permission).some((p: any) =>
          !p.povs || p.povs.toLowerCase().includes('organization') || p.povs.toLowerCase().includes('admin')
        )
      );

    // Extract fresh org and parkings
    const validOrgId = extractOrgId(userData);
    const assignedParkings = extractAssignedParkings(userData);
    const hasOrg = !!validOrgId;
    const hasParking = assignedParkings.length > 0;

    // Sync fresh user data to localStorage with cleaned org/parking fields
    if (isFreshFromServer && userData && (userData.id || userData._id)) {
      const mergedUser = {
        ...(apiService.getCurrentUser() || currentUser),
        ...userData,
        organization_id: validOrgId || undefined,
        org_id: validOrgId || undefined,
        organization: hasOrg ? (typeof userData.organization === 'object' ? userData.organization : { id: validOrgId }) : null,
        parkings: assignedParkings,
        parkingIds: assignedParkings.map((p: any) => (typeof p === 'object' ? (p?.id || p?.parking_id || p?._id) : p))
      };
      localStorage.setItem('user_info', JSON.stringify(mergedUser));
      try {
        (apiService as any).currentUserSubject?.next(mergedUser);
      } catch (e) { }
    }

    if (!isSuperAdminUser) {
      // Priority 1: If user has no organization -> redirect to /no-organization
      if (!hasOrg) {
        sessionStorage.removeItem('organization_id');
        localStorage.removeItem('organization_id');
        sessionStorage.removeItem('org_id');
        localStorage.removeItem('org_id');
        sessionStorage.removeItem('parking_id');
        sessionStorage.removeItem('parkingId');
        localStorage.removeItem('parking_id');
        localStorage.removeItem('parkingId');
        return router.createUrlTree(['/no-organization']);
      }

      // Parking-level restrictions ONLY apply to restricted non-org users
      if (!isOrgLevelUser) {
        // Priority 2: If user is in parking POV (or operator/vendor) and has no parking assigned -> redirect to /no-parking
        const currentPov = sessionStorage.getItem('user_pov') || userData?.user_pov;
        if ((currentPov === 'parking' || uType === 'vendor' || uType === 'operator') && !hasParking) {
          sessionStorage.removeItem('parking_id');
          sessionStorage.removeItem('parkingId');
          localStorage.removeItem('parking_id');
          localStorage.removeItem('parkingId');
          return router.createUrlTree(['/no-parking']);
        }

        // Priority 3: If on a /parking/:parkingId/... route, verify access to that parkingId
        const urlSegments = targetUrl.split('?')[0].split('/').filter(s => s.length > 0);
        if (urlSegments.length >= 2 && urlSegments[0] === 'parking') {
          const routeParkingId = urlSegments[1];
          const hasAccessToParking = assignedParkings.some((p: any) => {
            const pId = typeof p === 'object' ? (p?.id || p?.parking_id || p?._id) : p;
            return pId === routeParkingId;
          });
          if (!hasAccessToParking) {
            sessionStorage.removeItem('parking_id');
            sessionStorage.removeItem('parkingId');
            localStorage.removeItem('parking_id');
            localStorage.removeItem('parkingId');
            const fallbackParkingId = typeof assignedParkings[0] === 'object' ? (assignedParkings[0]?.id || assignedParkings[0]?.parking_id) : assignedParkings[0];
            return router.createUrlTree([hasParking && fallbackParkingId ? `/parking/${fallbackParkingId}/dashboard` : '/no-parking']);
          }
        }
      }
    }

    return true;
  };

  const isRecentlyValidated =
    lastValidatedUserId === currentUser.id && Date.now() - lastValidatedAt < USER_REVALIDATE_INTERVAL_MS;
  if (isRecentlyValidated) {
    return checkAccess(currentUser, false);
  }

  return apiService.getUserById(currentUser.id).pipe(
    timeout(5000), // 5s timeout
    map((response: any) => {
      lastValidatedUserId = currentUser.id;
      lastValidatedAt = Date.now();
      return checkAccess(response?.data || response, true);
    }),
    catchError((error) => {
      console.warn('️ Auth guard validation failed/timed out, allowing navigation anyway:', error);
      return of(true);
    })
  );
};
